import React from "react";

import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid, Typography, Box, Card, InputAdornment } from "@mui/material";
import { Button, IconButton, TextField } from "@mui/material";
import { GridCloseIcon, GridExpandMoreIcon } from "@mui/x-data-grid";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DataObjectIcon from '@mui/icons-material/DataObject';

import globalStyles from "../../../../../assets/styles/global.module.scss";
import styles from "../../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import { createWhatsappTemplatePayloadForTemplateLibrary } from "../../../../../models/axiosModel";
import { useTemplateLibraryStore } from "../../../../../store/templateLibraryStore";
import { areHeaderVariablesModified, canUpdateTemplate, getTextCharacterLimit, getVariableRegex, isCallPermissionRequestTemplate, isCatalogTemplate, isTemplateCategoryAuthentication, maxHeaderTextChars, renderInvalidVarParamWarning, renderTooManyVariableParameterWarning, renderWarningOfModifiedVar } from "../../../channels/global/whatsapp/templates/utils";
import { checkDisableAddVarButton, checkFlagError, disableHeaderType, headerTypeIconMap, isTemplateCreated, LOCATION_HEADER_FIELDS, getLocationHeaderSampleWarning } from "../../constant";
import { getWhatsappMediaAllowedFileTypes, getWhatsappMediaSizeLimit } from "../../../../../utils/constants";
import { Delete } from "@mui/icons-material";
import { whiteColor } from "../../../../../utils/colors";
import { useSearchParams } from "react-router-dom";
import { useCarouselFunctions } from "../../utils/useCarouselFunctions";
import Required from "../../../../utility/required";
import FileUpload from "../../../../utility/fileUpload/fileUpload";
import TouchTooltip from "../../utils/TouchTooltip";
import VariableDisclaimer from "../../utils/VariableDisclaimer";
import SampleVariableKeyLabel from "../../utils/SampleVariableKeyLabel";

const WhatsAppTemplateFormHeader = (props: any) => {
    const { handleChangeText, handleAddBodyVariableManually, removeVariableData, handleAddField, handleChange, handleRemoveField, 
        headerTextareaRef, uploadFileHandler, removeFileHandler, onChangeOfHeader, isFileUploaded, checkTypeOfVariableValidation,
        registerTextarea 
    } = props;

    const { isMobileScreen } = useCarouselFunctions();

    const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);
    const existingTemplateData = useTemplateLibraryStore((state) => state.existingTemplateData);
    const templateStatusDetails = useTemplateLibraryStore((state) => state.templateStatusDetails);
    const setCreateTemplatePayload = useTemplateLibraryStore((state) => state.setCreateTemplatePayload);
    const interactiveTypeList = useTemplateLibraryStore((state) => state.interactiveTypeList);
    const whatsappTemplateHeaderTypes = useTemplateLibraryStore((state) => state.whatsappTemplateHeaderTypes); 
    const files = useTemplateLibraryStore((state) => state.files);
    const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);
    const whatsappTemplateTypes = useTemplateLibraryStore((state) => state.whatsappTemplateTypes);

    const [searchParams] = useSearchParams();
    let draftId = searchParams.get('draft_id');

    // Constants
    const headerOptionsSelection = [
        {
            header_option_id: 0,
            header_option_value: "None"
        },
    ]

    const isAuthenticationTemplate = isTemplateCategoryAuthentication(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id);
    
    const isTemplateTypeCallPermissionRequest = isCallPermissionRequestTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id);
    const isTemplateTypeCatalog = isCatalogTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id);
    const selectedHeaderType = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name?.toLowerCase() ?? '';
    const locationHeaderVariableData =
        createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data ?? [];

    const getLocationVariable = (variableKey: string) =>
        locationHeaderVariableData.find(
            (variable: any) => variable?.whatsapp_template_dynamic_variable_key?.toLowerCase() === variableKey.toLowerCase()
        );

    const getLocationVariableSampleValue = (variableKey: string) =>
        getLocationVariable(variableKey)?.whatsapp_template_dynamic_variable_sample_value ?? '';

    const getLocationVariableKey = (variableKey: string) =>
        getLocationVariable(variableKey)?.whatsapp_template_dynamic_variable_key ?? variableKey;

    const locationHeaderWarningText = selectedHeaderType === "location"
        ? getLocationHeaderSampleWarning(locationHeaderVariableData)
        : "";

    // Functions
    const handleHeaderSampleVariableTextDisabled = () => {
        return (
            !canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId)
        )
    }

    const handleHeaderTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // const replacementString = `{{${createTemplatePayload.whatsapp_template_data.whatsapp_template_body.whatsapp_template_body_variable_data.length}}}`

        if (value.endsWith('{{')) {
            handleAddBodyVariableManually(value, "header")
        } 
        else {
            // If no double curly braces, update state normally 
            const transformedValue = value.replace(getVariableRegex(createTemplatePayload?.flag_named_parameter), '*'); // Variable is considered as one character.
            let maximumHeaderCharacterLimit = getTextCharacterLimit(createTemplatePayload, interactiveTypeList, "header text limit") ?? maxHeaderTextChars;

            if (transformedValue.length <= maximumHeaderCharacterLimit) {
                let finalValues = removeVariableData(value, "header")
                handleChangeText(event, 'header');
                
                const updatedPayload: createWhatsappTemplatePayloadForTemplateLibrary = {
                    ...createTemplatePayload,
                    whatsapp_template_data: {
                        ...createTemplatePayload?.whatsapp_template_data,
                        whatsapp_template_header: {
                            ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header,
                            whatsapp_template_header_data: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.map((variable: any) => {
                                return {
                                    ...variable,
                                    whatsapp_template_header_data_value: finalValues?.textAreaValue,
                                    whatsapp_template_header_variable_data: finalValues?.variableData,
                                }
                            })
                        }
                    }
                };
                
                setCreateTemplatePayload(updatedPayload);
            }
        }
    }
    
    if(isTemplateTypeCatalog) return null;

    return (
        <Accordion defaultExpanded={true} className={`${styles.accordion}`}>
            <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" className={styles.accordion__summary}>
                <Grid container justifyContent={"space-between"} alignItems={"center"}>
                    <span className={styles.accordion__summary__text}>
                        Header
                    </span>

                    {selectedHeaderType === "text" && (
                        <span className={styles.accordion__summary__value}>
                            {`${createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_value?.replace(/{{\d+}}/g, '*')?.length ?? 0}/${getTextCharacterLimit(createTemplatePayload, interactiveTypeList, "header text limit")}`}
                        </span>
                    )}

                    {["image", "video", "document", "gif", "location"].includes(selectedHeaderType) && (
                        <span className={styles.accordion__summary__value}>
                            {createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name}
                        </span>
                    )}
                </Grid>
            </AccordionSummary>

            <Divider sx={{ width: "100%", opacity: "1" }} />
            
            <AccordionDetails className={styles.accordion__details}>
                 
                <Grid item xs={12}>
                    <Grid container spacing={1.5}>
                        
                        {/* None option */}
                        {!isTemplateCreated(createTemplatePayload) && headerOptionsSelection?.map((option: any, ind: number) => {
                            const headerTypeName = option.header_option_value.toLowerCase();
                            const isSelected = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name === option.header_option_value;
                            const isDisabled = (createTemplatePayload?.whatsapp_template_id || isAuthenticationTemplate)
                            
                            return (
                                <Grid item key={ind}>
                                    <Card
                                        onClick={() => !isDisabled && onChangeOfHeader(option?.header_option_value)}
                                        className={`${styles.whatsappTemplateFormHeader__card} ${isSelected ? styles['whatsappTemplateFormHeader__card--selected'] : ''} ${isDisabled ? styles['whatsappTemplateFormHeader__card--disabled'] : ''}`}
                                    >
                                        <Box className={styles.whatsappTemplateFormHeader__card__icon}>
                                            {headerTypeIconMap[headerTypeName as keyof typeof headerTypeIconMap] || null}
                                        </Box>
                                        <Typography variant="caption" className={`${styles.whatsappTemplateFormHeader__card__text} ${isSelected ? styles['whatsappTemplateFormHeader__card__text--selected'] : ''}`}>
                                            {option?.header_option_value}
                                        </Typography>
                                    </Card>
                                </Grid>
                            );
                        })}

                        {!isTemplateCreated(createTemplatePayload) && whatsappTemplateHeaderTypes
                            ?.filter((headers: any) => isTemplateCreated(createTemplatePayload) || !disableHeaderType(headers?.whatsapp_template_header_type_name, createTemplatePayload, interactiveTypeList, isAuthenticationTemplate, isTemplateTypeCallPermissionRequest))
                            ?.map((headers: any, ind: number) => 
                            {
                                const headerTypeName = headers?.whatsapp_template_header_type_name?.toLowerCase();
                                const isSelected = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name === headers?.whatsapp_template_header_type_name;
                                const isDisabled = isTemplateCreated(createTemplatePayload) ? true : disableHeaderType(headers?.whatsapp_template_header_type_name);

                                return (
                                    <Grid item key={`header-${ind}`}>
                                        <Card
                                            onClick={() => !isDisabled && onChangeOfHeader(headers?.whatsapp_template_header_type_name)}
                                            className={`
                                                ${globalStyles.clickable} 
                                                ${styles.whatsappTemplateFormHeader__card} 
                                                ${isSelected ? styles['whatsappTemplateFormHeader__card--selected'] : ''} 
                                                ${isDisabled ? styles['whatsappTemplateFormHeader__card--disabled'] : ''}
                                            `}
                                        >
                                            <Box className={styles.whatsappTemplateFormHeader__card__icon}>
                                                {headerTypeIconMap[headerTypeName as keyof typeof headerTypeIconMap] || null}
                                            </Box>
                                            <Typography variant="caption" className={`${styles.whatsappTemplateFormHeader__card__text} ${isSelected ? styles['whatsappTemplateFormHeader__card__text--selected'] : ''}`}>
                                                {headers?.whatsapp_template_header_type_name}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                );
                            }
                        )}
                    </Grid>
                </Grid>
                

                {selectedHeaderType === "text" &&
                    <Grid item xs={12} className={styles.container}>
                        <Grid container>
                            
                            <Grid item xs={12} className={styles.templateFieldBetweenSpacing}>
                                <Typography component={'span'} className={styles.templateHeadtext}>
                                    Header Text
                                </Typography>
                                <Required />
                            </Grid>

                            <Grid item xs={12} className={styles.whatsappTemplateFormHeader__headerTextBox}>

                                <Grid item xs={12} className={`${styles.addVariableButtonContainer} ${styles.formattingContainer}`}>
                                    <Button
                                        variant="outlined"
                                        size='small'
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleAddField("header")}
                                        className={`${styles.callToActionButton} ${globalStyles.clickable}`}
                                        disabled={createTemplatePayload?.whatsapp_template_id ? true : checkDisableAddVarButton(createTemplatePayload)}
                                        startIcon={<DataObjectIcon />}
                                    >
                                        Add Variable
                                    </Button>
                                </Grid>

                                <TextField
                                    // disabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                                    disabled={!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId)}
                                    variant="outlined"
                                    ref={headerTextareaRef}
                                    inputRef={(el: HTMLInputElement | null) => {
                                        registerTextarea('header', el);
                                    }}
                                    value={createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_value ?? ''}
                                    size='small'
                                    // className={styles.templateGlobalTextfield}
                                    // className={styles.textarea}
                                    className={styles.whatsappTemplateFormHeader__headerTextField}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleHeaderTextChange(event)}
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        },
                                        margin: '10px 0px'
                                    }}
                                    inputProps={{ dir: 'auto' }}
                                    autoComplete='off'
                                    placeholder='Enter header text'
                                    FormHelperTextProps={{
                                        style: {
                                            textAlign: 'right',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }
                                    }}
                                />
                            </Grid>

                            {!checkTypeOfVariableValidation('header') && 
                                <Grid item sx={{ margin: '5px 0px 2px' }}>
                                    {renderInvalidVarParamWarning(createTemplatePayload?.flag_named_parameter)}
                                </Grid>
                            }

                            {checkFlagError("header", createTemplatePayload) &&
                                renderTooManyVariableParameterWarning()
                            }

                            {areHeaderVariablesModified(createTemplatePayload, existingTemplateData) && 
                                renderWarningOfModifiedVar()
                            }

                        </Grid>
                    </Grid>
                }
                
                {["image", "video", "document", "gif"].includes(selectedHeaderType) &&
                    <Grid item xs={12} className={styles.container} >
                        <Grid container>
                            
                            <Grid item xs={12} className={styles.templateFieldBetweenSpacing}>
                                <Typography component={'span'} className={styles.templateHeadtext}>
                                    {`Upload ${createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name}`}
                                </Typography>
                                <Required />

                                <TouchTooltip
                                    key="upload-header-tooltip"
                                    title={
                                        <span>
                                            Allowed Formats: {getWhatsappMediaAllowedFileTypes(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name || "")?.displayTypeToUser}
                                        </span>
                                    }
                                    placement={isMobileScreen ? 'top-start' : 'right'}
                                >
                                    <InfoOutlinedIcon className={styles.infoIcon} />
                                </TouchTooltip>
                            </Grid>

                            <Grid item xs={12} sx={{ margin: '5px 0px 0px' }}>
                                <FileUpload
                                    uploadedFileDetails={files}
                                    multipleFileAllow={true}
                                    // fileSizeAllowed={selectedHeaderType.whatsapp_template_header_type_name.toLowerCase() === "image" ? 3 : selectedHeaderType.whatsapp_template_header_type_name.toLowerCase() === "video" ? 16 : 2}
                                    fileSizeAllowed={getWhatsappMediaSizeLimit(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name || "")}
                                    // fileTypeAllowed={headerFileTypes(selectedHeaderType.whatsapp_template_header_type_name)}
                                    fileTypeAllowed={getWhatsappMediaAllowedFileTypes(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name || "")?.allowedType}
                                    fileNumberAllowed={2}
                                    isDisabled={createTemplatePayload.whatsapp_template_id ? true : false}
                                    uploadFileHandler={uploadFileHandler}
                                    removeFileHandler={removeFileHandler}
                                    isDeleteFileIconVisible={isFileUploaded}
                                    bgColor={whiteColor}
                                    flagWhatsapp={true}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                }

                {selectedHeaderType === "location" && 
                    <Grid item xs={12} className={styles.container} >
                        <Box className={styles.whatsappTemplateFormHeader__locationFieldsBox}>
                            <Grid container spacing={1.5}>
                                {LOCATION_HEADER_FIELDS.map((field) => {
                                    const variableKey = getLocationVariableKey(field.key);
                                    const sampleValue = getLocationVariableSampleValue(field.key);
                                    const isFieldDisabled = handleHeaderSampleVariableTextDisabled();

                                    return (
                                        <Grid item xs={12} key={field.key}>
                                            <Grid container sx={{ alignItems: 'center' }} flexWrap="nowrap">
                                                <Grid item className={`${styles.whatsappTemplateFormHeader__locationFieldLabel} ${styles.templateFieldBetweenSpacing}`}>
                                                    <Typography component={'span'} className={styles.templateHeadtext}>
                                                        {field.label}
                                                    </Typography>
                                                    {field.required && <Required />}
                                                </Grid>

                                                <Grid item xs>
                                                    <TextField
                                                        disabled={isFieldDisabled}
                                                        size="small"
                                                        variant="outlined"
                                                        className={styles.templateGlobalTextfield}
                                                        value={sampleValue}
                                                        onChange={(e) => handleChange(variableKey, e, "header")}
                                                        fullWidth
                                                        multiline={field.multiline}
                                                        minRows={field.multiline ? 2 : undefined}
                                                        maxRows={field.multiline ? 3 : undefined}
                                                        placeholder={field.placeholder}
                                                        autoComplete="off"
                                                        inputProps={{ dir: 'auto' }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                border: 'none'
                                                            },
                                                            '&.Mui-disabled, & .MuiInputBase-root.Mui-disabled, & .MuiInputBase-input.Mui-disabled': {
                                                                cursor: 'not-allowed'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            {!!locationHeaderWarningText && (
                                <Grid item xs={12} sx={{ marginTop: '12px' }}>
                                    <VariableDisclaimer text={locationHeaderWarningText} />
                                </Grid>
                            )}
                        </Box>
                    </Grid>
                }
                
                {/* {createTemplatePayload.whatsapp_template_data.whatsapp_template_body.whatsapp_template_body_variable_data.map((field: any) => ( */}
                {createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data &&
                    createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.length > 0 &&
                    selectedHeaderType === "text" &&
                    !["{{headerimage}}", "{{headervideo}}", "{{headergif}}"].includes(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data?.[0]?.whatsapp_template_dynamic_variable_key.toLowerCase()) &&
                    createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data[0]?.whatsapp_template_header_variable_data?.map((field: any, ind: number) => (
                        <React.Fragment key={ind}>
                            
                            <Grid item xs={12} sx={{ margin: '10px 0px' }}>
                                <Grid container sx={{ alignItems: 'center' }} flexWrap={"nowrap"}>
                                    
                                    <Grid item className={styles.sampleVariableKeyColumn}>
                                        <SampleVariableKeyLabel
                                            variableKey={`${field?.whatsapp_template_dynamic_variable_key}`}
                                            className={styles.sampleVariableKeyText}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} key={field.whatsapp_template_dynamic_variable_id}>
                                        <TextField
                                            // disabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                                            disabled={handleHeaderSampleVariableTextDisabled()}
                                            size='small'
                                            variant="outlined"
                                            className={styles.sampleVariableTextField}
                                            // value={field.value}
                                            onChange={(e) => handleChange(field?.whatsapp_template_dynamic_variable_key, e, "header")}
                                            fullWidth
                                            autoComplete='off'
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none'
                                                }
                                            }}
                                            value={field?.whatsapp_template_dynamic_variable_sample_value}
                                            inputProps={{ dir: 'auto' }}
                                            InputProps={{
                                                endAdornment: (field?.whatsapp_template_dynamic_variable_sample_value?.trim() !== '' && !handleHeaderSampleVariableTextDisabled()) ? (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            edge="end"
                                                            aria-label="Clear sample text"
                                                            onClick={() => handleChange(field?.whatsapp_template_dynamic_variable_key, { target: { value: '' } }, "header")}
                                                            className={globalStyles.clickable}
                                                        >
                                                            <GridCloseIcon className={styles.sampleVariableClearIcon} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item sx={{ marginLeft: '5px' }}>
                                        <IconButton onClick={() => handleRemoveField(field?.whatsapp_template_dynamic_variable_key, "header")} disabled={createTemplatePayload?.whatsapp_template_id ? true : false} className={styles.sampleVariableDeleteButton}>
                                            <Delete className={styles.sampleVariableDeleteIcon} />
                                        </IconButton>
                                    </Grid>

                                </Grid>
                            </Grid>

                        </React.Fragment>
                    ))
                }
                
                {/* {createTemplatePayload.whatsapp_template_data.whatsapp_template_body.whatsapp_template_body_variable_data.filter((data) => data.whatsapp_template_dynamic_variable_sample_value.trim() == "").length != 0 && */}
                {selectedHeaderType === "text" &&
                    createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data &&
                    createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.length > 0 &&
                    createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data[0]?.whatsapp_template_header_variable_data &&
                    createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data[0]?.whatsapp_template_header_variable_data?.length > 0 &&
                    createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data?.filter((data: any) => (data?.whatsapp_template_dynamic_variable_sample_value ?? '').trim() === '').length !== 0 &&
                    <Grid item sx={{ marginTop: '12px' }}>
                        <VariableDisclaimer text="Add sample text" />
                    </Grid>
                }

            </AccordionDetails>
        </Accordion>
    )

}

export default WhatsAppTemplateFormHeader;
